import { classColor, classIcon } from '../../theme';

/**
 * Product-facing character-creation choice. This model deliberately does not
 * import combat, ability, job, or progression data.
 *
 * `runtimeClassId` is the one compatibility seam with today's class-based
 * character contract. A future Helix calling/evolution model can replace that
 * adapter without changing the creator's presentation components.
 */
export interface CreationCallingDefinition {
  readonly id: string;
  readonly runtimeClassId: string;
  readonly name: string;
  readonly icon: string;
  readonly accent: string;
  readonly previewAnimation: 'anim-melee' | 'anim-ranged' | 'anim-magic';
  readonly epithet: string;
  readonly invocation: string;
  readonly promise: string;
  readonly discovery: string;
}

export const CREATION_CALLINGS = [
  {
    id: 'warrior',
    runtimeClassId: 'warrior',
    name: 'Steadfast',
    icon: classIcon('warrior'),
    accent: classColor('warrior'),
    previewAnimation: 'anim-melee',
    epithet: 'The Steadfast',
    invocation: 'When the road narrows, you are the one who does not move.',
    promise: 'You meet the unknown face-first, turning courage into shelter for everyone who follows.',
    discovery: 'What you choose to stand for will be written in the world.',
  },
  {
    id: 'mage',
    runtimeClassId: 'mage',
    name: 'Astromancer',
    icon: classIcon('mage'),
    accent: classColor('mage'),
    previewAnimation: 'anim-magic',
    epithet: 'The Star-Touched',
    invocation: 'Every strange light is an invitation.',
    promise: 'You follow wonder past the edge of certainty, listening for patterns hidden in impossible things.',
    discovery: 'The world will teach you which mysteries answer back.',
  },
  {
    id: 'hunter',
    runtimeClassId: 'hunter',
    name: 'Wayfinder',
    icon: classIcon('hunter'),
    accent: classColor('hunter'),
    previewAnimation: 'anim-ranged',
    epithet: 'The Wayfinder',
    invocation: 'No trail is truly empty to patient eyes.',
    promise: 'You belong to the long road, reading wind, tracks, and silence where others see only wilderness.',
    discovery: 'Your quarry, and the paths between, wait beyond the waking.',
  },
  {
    id: 'paladin',
    runtimeClassId: 'paladin',
    name: 'Dawnward',
    icon: classIcon('paladin'),
    accent: classColor('paladin'),
    previewAnimation: 'anim-melee',
    epithet: 'The Dawnward',
    invocation: 'A small light is still a promise.',
    promise: 'You carry hope into places that have forgotten its shape and stand where the dark presses closest.',
    discovery: 'The vows worth keeping will be tested in the world.',
  },
  {
    id: 'rogue',
    runtimeClassId: 'rogue',
    name: 'Unseen',
    icon: classIcon('rogue'),
    accent: classColor('rogue'),
    previewAnimation: 'anim-melee',
    epithet: 'The Unseen',
    invocation: 'Some doors prefer a quieter kind of courage.',
    promise: 'You notice the loose stone, the guarded glance, and the second path nobody thought to watch.',
    discovery: 'Every secret leaves a seam. The world is full of them.',
  },
  {
    id: 'shaman',
    runtimeClassId: 'shaman',
    name: 'Stormspeaker',
    icon: classIcon('shaman'),
    accent: classColor('shaman'),
    previewAnimation: 'anim-magic',
    epithet: 'The Stormspeaker',
    invocation: 'The sky has a voice. So does the stone.',
    promise: 'You listen where others command, walking beside the old tempers of rain, ember, and earth.',
    discovery: 'Which spirits answer you can only be learned on the road.',
  },
  {
    id: 'priest',
    runtimeClassId: 'priest',
    name: 'Lightbearer',
    icon: classIcon('priest'),
    accent: classColor('priest'),
    previewAnimation: 'anim-magic',
    epithet: 'The Lightbearer',
    invocation: 'Mercy is brightest where it is hardest to offer.',
    promise: 'You tend the fragile flame in others, even when your own path passes through shadow.',
    discovery: 'Faith will take its meaning from the lives you touch.',
  },
  {
    id: 'warlock',
    runtimeClassId: 'warlock',
    name: 'Pactbound',
    icon: classIcon('warlock'),
    accent: classColor('warlock'),
    previewAnimation: 'anim-magic',
    epithet: 'The Pactbound',
    invocation: 'When the lantern gutters, something answers.',
    promise: 'You listen at doors others leave unopened, drawn by the knowledge and danger waiting beyond the firelight.',
    discovery: 'Every bargain has a shape. Yours waits in the world.',
  },
  {
    id: 'druid',
    runtimeClassId: 'druid',
    name: 'Wildhearted',
    icon: classIcon('druid'),
    accent: classColor('druid'),
    previewAnimation: 'anim-magic',
    epithet: 'The Wildhearted',
    invocation: 'Nothing living keeps only one shape.',
    promise: 'You move with root, rain, and restless green things, guarding the quiet agreements that hold the wild together.',
    discovery: 'The land will show you what it needs, if you learn to listen.',
  },
] as const satisfies readonly CreationCallingDefinition[];

export type CreationCalling = (typeof CREATION_CALLINGS)[number];
export type CreationCallingId = CreationCalling['id'];

export function findCreationCalling(callingId: string): CreationCalling | undefined {
  const norm = callingId.toLowerCase();
  return CREATION_CALLINGS.find(calling =>
    calling.id === norm ||
    calling.runtimeClassId === norm ||
    calling.name.toLowerCase() === norm,
  );
}
